<template>
  <header class="project-header" ref="projectHeader">
    <h1 class="title text-3xl">
      <span class="inner">{{ data.title }}</span>
    </h1>
    <p class="location text-3xl-display">
      <span class="inner">({{ data.location }})</span>
    </p>
    <div class="facts">
      <div v-if="data.services" class="figure">
        <p class="description text-md">Services</p>
        <p class="text-md-regular fact">{{ data.services }}</p>
      </div>
      <div v-if="data.competences" class="figure">
        <p class="description text-md">Kompetenzen</p>
        <p class="text-md-regular fact">{{ data.competences }}</p>
      </div>
      <div v-if="data.customer" class="figure">
        <p class="description text-md">Kunde</p>
        <p class="text-md-regular fact">{{ data.customer }}</p>
      </div>
      <div v-if="data.year" class="figure">
        <p class="description text-md">Jahr</p>
        <p class="text-md-regular fact">{{ data.year }}</p>
      </div>
    </div>
    <div class="imagewrapper">
      <ImageWithCaption
        v-if="data.imageLandscape"
        class="imagewithcaption"
        :data="data.imageLandscape"
        :no-placeholder="true"
      />
    </div>
  </header>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import ImageWithCaption from '~/components/partials/ImageWithCaption.vue';
import type { ImageWithCaptionFragment } from '#gql';
import useWait from '~/utils/useWait';
import CustomEase from 'gsap/CustomEase';

gsap.registerPlugin(CustomEase);

defineProps<{
  data: {
    location?: string | null;
    services?: string | null;
    competences?: string | null;
    customer?: string | null;
    year?: string | null;
    title?: string | null;
    imageLandscape?: ImageWithCaptionFragment | null;
  };
}>();

const { isVl } = useViewportSize();

const projectHeader = ref<HTMLElement>();

onMounted(async function () {
  await nextTick();

  // Register custom ease
  const customEaseOut = CustomEase.create('custom', '0.75, 0, 0.25, 1');

  // HTMLElements
  const imageWrapperElement = projectHeader.value?.querySelector(
    '.imagewrapper',
  ) as HTMLElement;
  const imageElement = imageWrapperElement.querySelector(
    '.imagewithcaption',
  ) as HTMLElement;
  const assetElement = imageElement.querySelector('picture') as HTMLElement;

  // Timeline
  const tl = gsap.timeline({
    paused: true,
    defaults: {
      duration: 1,
      ease: customEaseOut,
    },
  });

  // entry animation for page transition home -> project
  if (window.PageTransitionData?.featuredProjectRect && isVl.value) {
    // Get final image position
    const imageWrapperBox = imageWrapperElement.getBoundingClientRect();

    // Set initial image and asset position and make wrapper visible
    gsap.set(imageWrapperElement, {
      visibility: 'visible',
    });

    gsap.set(imageElement, {
      position: 'absolute',
      overflow: 'hidden',
      top: `${window.PageTransitionData.featuredProjectRect?.top}px`,
      left: `${window.PageTransitionData.featuredProjectRect?.left}px`,
      width: `${window.PageTransitionData.featuredProjectRect?.width}px`,
      height: `${window.PageTransitionData.featuredProjectRect?.height}px`,
    });

    gsap.set(assetElement, {
      scaleX: window.PageTransitionData.featuredProjectAssetScale.scaleX,
      scaleY: window.PageTransitionData.featuredProjectAssetScale.scaleY,
    });

    // Same amount as onLeave animation in pages/index.vue
    await useWait(250);

    // Move image and asset to final position
    tl.to(
      imageElement,
      {
        top: imageWrapperBox.top,
        left: imageWrapperBox.left,
        width: imageWrapperBox.width,
        height: imageWrapperBox.height,
      },
      0,
    );

    tl.to(
      assetElement,
      {
        top: 0,
        left: 0,
        width: imageWrapperBox.width,
        height: imageWrapperBox.height,
      },
      0,
    );
  } else {
    // When the page is loaded without page transition
    gsap.set(imageWrapperElement, {
      visibility: 'visible',
      position: 'relative',
    });
    tl.fromTo(imageElement, { opacity: 0 }, { opacity: 1 }, '<+=0.75');
  }

  // Title and location animations
  tl.fromTo(
    '.project-header .title .inner',
    { yPercent: 120, opacity: 1 },
    { yPercent: 0, opacity: 1 },
    0,
  );
  tl.fromTo(
    '.project-header .location .inner',
    { yPercent: -120, opacity: 1 },
    { yPercent: 0, opacity: 1 },
    '<',
  );
  tl.fromTo(
    '.project-header .facts',
    { yPercent: 100 },
    { yPercent: 0 },
    '<+=0.25',
  );
  tl.fromTo('.project-header .facts', { opacity: 0 }, { opacity: 1 }, '<+=0.5');

  // Play all animations
  tl.play();

  // Reset PageTransitionData
  window.PageTransitionData = null;
});
</script>

<style lang="scss"></style>

<style scoped lang="scss">
.project-header {
  padding: var(--base-component-padding);

  > .title,
  > .location {
    text-align: center;
    overflow-y: clip;

    > .inner {
      display: block;
      opacity: 0;
    }
  }

  > .title {
    padding-top: 8.5rem;

    @media (--vs) {
      padding-bottom: 0.25rem;
    }

    @media (--vl) {
      padding-bottom: 0.5rem;
    }
  }

  > .location {
    padding-bottom: 7.5rem;

    > .inner {
      opacity: 0;
    }
  }

  > .facts {
    z-index: -1;
    display: grid;
    gap: var(--base-component-padding-inline);
    opacity: 0;
    padding: 0 0 1.5rem 0;

    @media (--vs) {
      grid-template-columns: 1fr;
    }
    @media (--vl) {
      grid-template-columns: 2fr 2fr 2fr 1fr;
    }

    > .figure {
      > .description {
        padding-bottom: 0.25rem;
      }

      > .fact {
        color: var(--color-stone-500, #6c6a6a);
      }
    }
  }

  > .imagewrapper {
    --width: calc(
      100vw - var(--base-component-padding-inline) * 2 - var(
          --site-outer-margin,
          0
        ) * 2
    );
    --height: calc(var(--width) / 3 * 2);

    width: var(--width);
    height: var(--height);
    visibility: hidden;

    @media (--vs) {
      position: relative;
    }

    > .imagewithcaption {
      position: absolute;
      inset: 0;
      align-self: flex-start;
      color: var(--color-stone-500, #6c6a6a);
      z-index: 1;
      width: var(--width);
      height: var(--height);

      :deep(img) {
        height: 100% !important;
        max-width: 100% !important;
        object-fit: cover;
      }
    }

    //&:not(.-showcaption) {
    > .imagewithcaption {
      :deep(figcaption) {
        display: none;
      }
    }
    //}
  }
}
</style>
